import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Modal } from "@/components/ui/customs/modal";
import {
  ExpeditionDocumentFileBoDto,
  ExpeditionDocumentFileDto,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { Loader2 } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ExpeditionProcessStatusEnum,
  PRE_FINALIZATION_STATUSES,
} from "@/constants/enums/expeditionProcessStatus.enum";

type EditFileModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: ExpeditionDocumentFileBoDto | ExpeditionDocumentFileDto;
  onToggleSendViaApi?: (fileId: string, value: boolean) => Promise<void>;
  onToggleIsPrivate?: (fileId: string, value: boolean) => Promise<void>;
  expedition?: GetOneExpeditionBoResponseDto;
};

export const EditFileModal = ({
  open,
  onOpenChange,
  file,
  onToggleSendViaApi,
  onToggleIsPrivate,
  expedition,
}: EditFileModalProps) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  const sendViaApiState = "sendViaApi" in file && file.sendViaApi;
  const isPrivateState = "isPrivate" in file && file.isPrivate;
  const [sendViaApi, setSendViaApi] = useState(sendViaApiState);
  const [isPrivate, setIsPrivate] = useState(isPrivateState);
  const [isPending, setIsPending] = useState(false);

  const handleClose = () => {
    setSendViaApi(sendViaApiState);
    setIsPrivate(isPrivateState);
    onOpenChange(false);
  };

  const handleSave = async () => {
    setIsPending(true);
    try {
      if (onToggleSendViaApi && sendViaApi !== sendViaApiState) {
        await onToggleSendViaApi(file.id, sendViaApi);
      }
      if (onToggleIsPrivate && isPrivate !== isPrivateState) {
        await onToggleIsPrivate(file.id, isPrivate);
      }
      onOpenChange(false);
    } catch {
      toast.error(t("editFileModal.updateError"));
    } finally {
      setIsPending(false);
    }
  };

  const expeditionProcessStatus = expedition?.processStatus as ExpeditionProcessStatusEnum;
  const isPreFinalization = PRE_FINALIZATION_STATUSES.includes(expeditionProcessStatus);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("editFileModal.title")}
      size="sm"
      contentClassName="overflow-x-hidden"
      footer={
        <>
          <Button variant="outline" type="button" onClick={handleClose} disabled={isPending}>
            {t("common:actions.cancel")}
          </Button>
          <Button type="button" onClick={handleSave} disabled={isPending}>
            {isPending ? <Loader2 className="animate-spin" /> : t("editFileModal.submit")}
          </Button>
        </>
      }
    >
      <p className="text-muted-foreground min-w-0 truncate text-sm" title={file.name}>
        {file.name}
      </p>
      <div className="flex flex-col gap-4">
        {onToggleSendViaApi && (
          <div className="flex flex-col items-center justify-between gap-2 rounded-lg border p-3">
            <div className="flex w-full flex-row items-center justify-between gap-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{t("editFileModal.sendViaApiLabel")}</span>
                <span className="text-xs text-gray-500">
                  {sendViaApi ? t("editFileModal.linkedToApi") : t("editFileModal.notLinkedToApi")}
                </span>
              </div>
              <Switch checked={sendViaApi} onCheckedChange={setSendViaApi} />
            </div>
            {sendViaApi &&
              expedition &&
              (isPreFinalization ? (
                <span className="text-xs text-gray-500">
                  {t("editFileModal.preFinalizationNotice")}
                </span>
              ) : (
                <span className="text-xs text-gray-500">
                  <Trans
                    t={t}
                    i18nKey="editFileModal.postFinalizationNotice"
                    components={{ br: <br /> }}
                  />
                </span>
              ))}
          </div>
        )}

        {onToggleIsPrivate && (
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">{t("editFileModal.privateLabel")}</span>
              <span className="text-xs text-gray-500">
                {isPrivate
                  ? t("editFileModal.hiddenFromClient")
                  : t("editFileModal.visibleToClient")}
              </span>
            </div>
            <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
          </div>
        )}
      </div>
    </Modal>
  );
};
